/**
 * The product editor's vendor list, as supplier-picker options -- ONE loader.
 *
 * WHY THIS EXISTS (Jake, 2026-10-21)
 * ----------------------------------
 * Clicking through from a decoration's "Where it's used" row to a product,
 * the Supplier, SKU & Inventory page showed the supplier as "sanmar" instead
 * of "SanMar", and details, inventory, sizes, cost and previews all failed.
 * The stored supplier was fine everywhere (prod, stage, dev, every sandbox:
 * every `campaign_products.supplier_name` matches its vendor's name). What
 * failed was the vendor list -- the only place the editor learns a product's
 * supplier KEY -- and reproducing on 8085 by failing that one request gave
 * exactly the report:
 *
 *   * The editor shell and SupplierSkuPanel each fetched the list on their
 *     own, once, with no retry. A single failed request was the final word.
 *   * The shell turned the failure into "no suppliers", so the product
 *     resolved to no key: an empty catalog (no colors, no sizes) and no
 *     Artifi previews.
 *   * The panel kept its pre-load default KEY, 'sanmar', with no option to
 *     name it -- which a v-select renders verbatim -- and guessed SanMar for
 *     every lookup.
 *
 * So this is the one loader both use (the shell provides it, the panel
 * injects it): a transient failure is retried with a short back-off, a final
 * failure is a distinct FAILED state rather than an empty list, and
 * `reload()` recovers -- every computed that reads `options` re-resolves.
 *
 * WHAT COUNTS AS TRANSIENT
 * ------------------------
 * No response at all (network error, an API restarting behind its proxy),
 * a 5xx, or a 429. A 4xx is an answer, not an outage: retrying it changes
 * nothing, so it fails at once.
 */
import { computed, getCurrentInstance, inject, onUnmounted, ref } from 'vue'
import { getVendorList } from '@/services/vendorService'
import { toSupplierOptions } from '@/utils/supplier-entitlement'
import { describeError } from "@/utils/safeErrorLog";

/** provide/inject key: the editor shell provides its loader to the panels. */
export const SUPPLIER_OPTIONS_KEY = Symbol('supplierOptions')

/** Back-off before each retry; its length is the retry count. */
export const VENDOR_LIST_RETRY_DELAYS_MS = Object.freeze([1000, 3000])

const STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
})

function defaultSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isTransient(err) {
  const status = err?.response?.status
  if (status == null) return true
  return status >= 500 || status === 429
}

/**
 * Build a vendor-list loader.
 *
 * `fetchVendors`, `retryDelaysMs` and `sleep` are injectable for tests;
 * production callers pass nothing.
 */
export function createSupplierOptions({
  fetchVendors = getVendorList,
  retryDelaysMs = VENDOR_LIST_RETRY_DELAYS_MS,
  sleep = defaultSleep,
} = {}) {
  // Picker options; null until a response has LANDED. Never set to [] on a
  // failure: "the server offered nothing" and "we never heard back" need
  // opposite handling (see SupplierSkuPanel's empty-state copy).
  const options = ref(null)
  // The UNFILTERED response length, recorded before entitlement filtering.
  // Null until a response lands. See SupplierSkuPanel.syncSupplierSelection.
  const vendorRowCount = ref(null)
  const status = ref(STATUS.IDLE)

  let inflight = null
  let disposed = false

  async function fetchWithRetry() {
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await fetchVendors({})
        const vendors = response?.data?.vendors ?? []
        vendorRowCount.value = vendors.length
        options.value = toSupplierOptions(vendors)
        status.value = STATUS.LOADED
        return
      } catch (err) {
        const canRetry = attempt < retryDelaysMs.length && isTransient(err)
        if (canRetry) {
          await sleep(retryDelaysMs[attempt])
          if (!disposed) continue
        }
        if (disposed) return
        // Loud in the console, explicit in the state. No hardcoded fallback
        // list: that is the literal SupplierSkuPanel removed, and it would
        // resurrect on every transient error.
        console.warn('[supplierOptions] vendor list unavailable:', describeError(err))
        status.value = STATUS.FAILED
        return
      }
    }
  }

  /** Load once. Shares an in-flight request; a no-op once loaded. */
  function load() {
    if (inflight) return inflight
    if (status.value === STATUS.LOADED) return Promise.resolve()
    disposed = false
    status.value = STATUS.LOADING
    inflight = fetchWithRetry().finally(() => {
      inflight = null
    })
    return inflight
  }

  /** Fetch again (the Retry button), keeping any options already held. */
  function reload() {
    if (inflight) return inflight
    status.value = STATUS.IDLE
    return load()
  }

  /** Stop retrying -- the editor that owns this loader is going away. */
  function dispose() {
    disposed = true
  }

  return {
    options,
    vendorRowCount,
    status,
    loaded: computed(() => status.value === STATUS.LOADED),
    failed: computed(() => status.value === STATUS.FAILED),
    settled: computed(
      () => status.value === STATUS.LOADED || status.value === STATUS.FAILED,
    ),
    load,
    reload,
    dispose,
  }
}

/**
 * The loader the editor shell provided, or a fresh one when mounted on its
 * own (a panel under test, or any future host that does not provide one).
 */
export function useSupplierOptions() {
  const provided = inject(SUPPLIER_OPTIONS_KEY, null)
  if (provided) return provided
  // Our own loader, so ours to stop: no retry outlives this component.
  const own = createSupplierOptions()
  if (getCurrentInstance()) onUnmounted(own.dispose)
  return own
}
